<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ExplorerNode } from '../types/entities';
import { readBooleanMap } from '../lib/persistedState';

interface TreeItem extends ExplorerNode { children: TreeItem[] }
const props = defineProps<{ node: TreeItem; allNodes: ExplorerNode[]; sceneNarrativeLabels?: Record<string, string>; draggedId?: string | null; draggedEntityId?: string | null; treeCommand?: { state: 'expand' | 'collapse'; revision: number }; searching?: boolean }>();
const emit = defineEmits<{
  openEntity: [entityId: string];
  move: [nodeId: string, parentId: string | null];
  rename: [node: ExplorerNode];
  deleteFolder: [node: ExplorerNode];
  dragStart: [nodeId: string];
  drop: [targetNodeId: string];
  dropEntity: [targetNodeId: string];
}>();
function readExpansion() {
  return readBooleanMap(localStorage, 'chronis.explorer.node-state.v2');
}
const savedExpansion = readExpansion();
const expanded = ref(savedExpansion[`node-${props.node.id}`] ?? false);
const manage = ref(false);
const parent = ref(props.node.parentId ?? '');
const icon = computed(() => props.node.kind === 'folder' ? '▾' : props.node.children.length ? '▾' : '·');
const hasExpandableContent = computed(() => props.node.children.length > 0);
const displayExpanded = computed(() => props.searching && hasExpandableContent.value ? true : expanded.value);
watch(() => props.treeCommand?.revision, () => {
  if (props.treeCommand?.revision && hasExpandableContent.value) {
    expanded.value = props.treeCommand.state === 'expand';
    setExpansion(`node-${props.node.id}`, expanded.value);
  }
}, { immediate: true });

function move() { emit('move', props.node.id, parent.value || null); manage.value = false; }
function setExpansion(key: string, value: boolean) {
  const next = { ...savedExpansion, [key]: value };
  Object.assign(savedExpansion, next);
  try { localStorage.setItem('chronis.explorer.node-state.v2', JSON.stringify(next)); } catch { /* Speicher ist optional. */ }
}
function toggleNode() { expanded.value = !expanded.value; setExpansion(`node-${props.node.id}`, expanded.value); }
function handleDrop() {
  if (props.draggedId && props.draggedId !== props.node.id) emit('drop', props.node.id);
  else if (props.draggedEntityId) emit('dropEntity', props.node.id);
}
function descendantEntityIds(node: TreeItem): string[] {
  return [node.targetEntityId, ...node.children.flatMap(descendantEntityIds)].filter((id): id is string => Boolean(id));
}
function startNativeDrag(event: DragEvent) {
  if (props.node.kind === 'folder') {
    const entityIds = [...new Set(descendantEntityIds(props.node))];
    if (!entityIds.length) { event.preventDefault(); return; }
    event.dataTransfer?.setData('application/x-chronis-entity-ids', JSON.stringify(entityIds));
    event.dataTransfer?.setData('text/plain', props.node.label);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
    return;
  }
  if (!props.node.targetEntityId) { event.preventDefault(); return; }
  event.dataTransfer?.setData('application/x-chronis-entity', props.node.targetEntityId);
  event.dataTransfer?.setData('application/x-chronis-entity-ids', JSON.stringify([props.node.targetEntityId]));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
  emit('dragStart', props.node.id);
}
</script>

<template>
  <li class="explorer-node">
    <div class="explorer-node-row" :class="{ 'drop-target': (draggedId && draggedId !== node.id) || draggedEntityId }" @dragover.prevent @drop.prevent="handleDrop">
      <button v-if="hasExpandableContent" type="button" class="tree-toggle" :aria-label="displayExpanded ? 'Einklappen' : 'Ausklappen'" :disabled="searching" @click="toggleNode">{{ displayExpanded ? '▾' : '▸' }}</button>
      <span v-else class="tree-toggle placeholder">{{ icon }}</span>
      <button type="button" class="tree-node-label" :title="node.entityAiSummary ?? (node.kind === 'folder' ? `${node.label} · in den Beziehungsgraphen ziehen` : node.label)" :draggable="node.kind === 'entity' || node.kind === 'folder'" @dragstart="startNativeDrag" @click="node.kind === 'entity' && node.targetEntityId ? emit('openEntity', node.targetEntityId) : hasExpandableContent ? toggleNode() : (manage = !manage)">
        <span>{{ node.kind === 'folder' ? '▣' : '◈' }}</span><span v-if="node.kind === 'entity'" class="entity-explorer-avatar" :class="{ empty: !node.entityProfileImageData }" aria-hidden="true"><img v-if="node.entityProfileImageData" :src="node.entityProfileImageData" alt="" /></span><small v-if="node.targetEntityId && sceneNarrativeLabels?.[node.targetEntityId]" class="tree-scene-position">[{{ sceneNarrativeLabels[node.targetEntityId] }}]</small>{{ node.label }}
      </button>
      <button type="button" class="tree-node-delete" :title="node.kind === 'folder' ? 'Ordner löschen' : 'Aus Ablage löschen'" :aria-label="node.kind === 'folder' ? 'Ordner löschen' : 'Aus Ablage löschen'" @click.stop="emit('deleteFolder', node)">🗑</button>
      <button type="button" class="tree-node-menu" title="Anordnen" @click="manage = !manage">⋯</button>
    </div>
    <div v-if="manage" class="tree-node-tools">
      <select v-model="parent" @change="move">
        <option value="">Stammebene</option>
        <option v-for="choice in allNodes.filter((item) => item.id !== node.id)" :key="choice.id" :value="choice.id">{{ choice.kind === 'folder' ? 'Ordner: ' : 'Entity: ' }}{{ choice.label }}</option>
      </select>
      <button v-if="node.kind === 'entity' && node.parentId" type="button" @click="emit('move', node.id, null); manage = false">Aus Ordner entfernen</button>
      <button v-if="node.kind === 'folder'" type="button" @click="emit('rename', node)">Umbenennen</button>
      <button v-if="node.kind === 'folder'" type="button" class="danger-text" @click="emit('deleteFolder', node)">Ordner löschen</button>
      <button v-if="node.kind === 'entity'" type="button" class="danger-text" @click="emit('deleteFolder', node)">Aus Ablage löschen</button>
    </div>
    <ul v-if="displayExpanded && node.children.length" class="explorer-children">
      <ExplorerTreeNode v-for="child in node.children" :key="child.id" :node="child" :all-nodes="allNodes" :scene-narrative-labels="sceneNarrativeLabels" :dragged-id="draggedId" :dragged-entity-id="draggedEntityId" :tree-command="treeCommand" :searching="searching" @open-entity="emit('openEntity', $event)" @move="(nodeId, parentId) => emit('move', nodeId, parentId)" @rename="emit('rename', $event)" @delete-folder="emit('deleteFolder', $event)" @drag-start="emit('dragStart', $event)" @drop="emit('drop', $event)" @drop-entity="emit('dropEntity', $event)" />
    </ul>
  </li>
</template>

<style scoped>
.tree-scene-position { margin-right:4px; color:var(--ui-action-text); font-size:.78em; font-weight:800; white-space:nowrap; }
</style>
